import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FaUserCircle,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePortalAccess } from '../hooks/usePortalAccess';
import { useSidebar } from '../contexts/SidebarContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getClientAccountData, PORTAL_PAGE_MAPPING } from '../services/portalAccessService';
import { PORTAL_SEARCH_ENTRIES } from '../data/portalSearchIndex';
import { loadPortalContentSearchEntries } from '../services/portalSearchContent';
import { useCompanyId } from '../hooks/useCompanyId';
import "./Sidebar.css";

const LOCKED_SEARCH_PATH_PREFIXES = [
  '/portal/roadmap',
  '/portal/profile',
  '/portal/checkout',
  '/portal/programdetail',
  '/portal/customprogram',
  '/portal/billing',
  '/portal/contract',
];

function getLongestMappedPrefix(pathname) {
  const path = pathname.replace(/\/$/, '') || '/portal';
  let best = '';
  for (const route of Object.keys(PORTAL_PAGE_MAPPING)) {
    if (path === route || path.startsWith(`${route}/`)) {
      if (route.length > best.length) best = route;
    }
  }
  return best || null;
}

function pathnameOnly(route) {
  const s = String(route || '');
  const q = s.indexOf('?');
  const h = s.indexOf('#');
  const end = q >= 0 && h >= 0 ? Math.min(q, h) : q >= 0 ? q : h >= 0 ? h : s.length;
  return s.slice(0, end);
}

function isPortalSearchPathAllowed(pathname, isPortalLocked, isPageAccessible, isLockedPageHidden) {
  const path = pathnameOnly(pathname).replace(/\/$/, '') || '/portal';
  const mapped = getLongestMappedPrefix(path);
  if (isPortalLocked) {
    if (path === '/portal') return true;
    const allowedWhileLocked = LOCKED_SEARCH_PATH_PREFIXES.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    if (!allowedWhileLocked) return false;
    // An explicit Access-tab toggle still wins over the locked allowlist.
    return !(mapped && isLockedPageHidden(mapped));
  }
  if (mapped) return isPageAccessible(mapped);
  return true;
}

const PortalSidebar = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { isPageAccessible, isLockedPageHidden, isPortalLocked, isPendingStart, loading: portalAccessLoading } = usePortalAccess();
  const { companyId, loading: companyIdLoading } = useCompanyId();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [userProfile, setUserProfile] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const [hiddenItems, setHiddenItems] = useState([]);
  const navigationRef = useRef(null);
  const seeMoreRef = useRef(null);
  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const lastSearchDiagRef = useRef('');
  const lastSkipDiagRef = useRef('');
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUser]);

  // Refresh profile data when location changes (user navigates)
  useEffect(() => {
    if (!currentUser) return;

    const refreshProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserProfile(userData);
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    };

    // Refresh when user navigates (this will catch profile updates)
    refreshProfile();
  }, [currentUser, location.pathname]);

  // Load account data for company name (and fallback company colors from account if needed)
  useEffect(() => {
    const loadAccount = async () => {
      if (!currentUser) return;
      try {
        const data = await getClientAccountData(currentUser.uid);
        setAccountData(data || null);
      } catch (error) {
        console.error('Error loading account data:', error);
        setAccountData(null);
      }
    };
    loadAccount();
  }, [currentUser]);

  // Individual materials, SOPs, KPIs, job descriptions, automations, JobTread
  // imports, Claude skills, courses/lessons, software tools and GPTs are all
  // CRM-managed, so their names are pulled at runtime and folded into the
  // search index. Loading waits until the user actually engages the search
  // box — it's several collection reads.
  const [contentEntries, setContentEntries] = useState([]);
  const [contentRequested, setContentRequested] = useState(false);
  useEffect(() => {
    if (!searchFocused && !searchQuery) return undefined;
    if (contentRequested || companyIdLoading) {
      const diag = `[Portal search] index load skipped (requested=${contentRequested}, companyIdLoading=${companyIdLoading})`;
      if (diag !== lastSkipDiagRef.current) {
        lastSkipDiagRef.current = diag;
        console.warn(diag);
      }
      return undefined;
    }
    setContentRequested(true);
    // No per-effect cancellation here: deps change on every keystroke, and a
    // keystroke must not discard the in-flight index (that made content search
    // work only when the user typed nothing until the load finished). Only a
    // real unmount (mountedRef) invalidates the result.
    loadPortalContentSearchEntries(companyId)
      .then((entries) => {
        if (!mountedRef.current) return;
        setContentEntries(entries);
        // A failed/empty load may un-cache itself — allow this sidebar to retry
        // on the next search engagement instead of staying empty until reload.
        if (!entries.length) setContentRequested(false);
      })
      .catch((err) => {
        console.error('Error loading portal content for search:', err);
        if (mountedRef.current) setContentRequested(false);
      });
  }, [searchFocused, searchQuery, contentRequested, companyId, companyIdLoading]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 1 || portalAccessLoading) return [];
    // Title hits rank above section hits, which rank above keyword-only hits —
    // otherwise a page's keyword list can bury the item the user typed.
    const scored = [];
    for (const entry of [...PORTAL_SEARCH_ENTRIES, ...contentEntries]) {
      if (!isPortalSearchPathAllowed(entry.gate || entry.path, isPortalLocked, isPageAccessible, isLockedPageHidden)) continue;
      const title = String(entry.title || '').toLowerCase();
      let score;
      if (title === q) score = 0;
      else if (title.startsWith(q)) score = 1;
      else if (title.includes(q)) score = 2;
      else if (String(entry.section || '').toLowerCase().includes(q)) score = 3;
      else if (String(entry.keywords || '').includes(q)) score = 4;
      else continue;
      scored.push({ entry, score });
    }
    const diag = `[Portal search] query="${q}" matches=${scored.length} (index: ${PORTAL_SEARCH_ENTRIES.length} pages + ${contentEntries.length} content, locked=${isPortalLocked})`;
    if (diag !== lastSearchDiagRef.current) {
      lastSearchDiagRef.current = diag;
      console.warn(diag);
    }
    return scored
      .sort((a, b) => a.score - b.score)
      .slice(0, 12)
      .map((row) => row.entry);
  }, [searchQuery, isPortalLocked, isPageAccessible, isLockedPageHidden, portalAccessLoading, contentEntries]);

  useEffect(() => {
    if (!searchFocused) return;
    const onDoc = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [searchFocused]);


  const menuItems = [
    { icon: '🏠', text: 'Dashboard', path: '/portal', alwaysVisible: true, alwaysVisibleWhenLocked: true },
    { icon: '🗺️', text: 'Roadmap', path: '/portal/roadmap', accessKey: 'roadmap', alwaysVisibleWhenLocked: true },
    { icon: '🎓', text: 'Courses', path: '/portal/courses', accessKey: 'courses' },
    { icon: '✅', text: 'Tasks', path: '/portal/tasks', accessKey: 'tasks' },
    { icon: '📅', text: 'Calendar', path: '/portal/portal-calendar', accessKey: 'calendar' },
    { icon: '💬', text: 'Community', path: '/portal/community', accessKey: 'community' },
    { icon: '🚀', text: 'Sales', path: '/portal/sales', accessKey: 'sales' },
    { icon: '⚙️', text: 'Operations', path: '/portal/operations', accessKey: 'operations' },
    { icon: '🏗️', text: 'JobTread', path: '/portal/jobtread', accessKey: 'operations' },
    { icon: '🤖', text: 'Technology', path: '/portal/technology', accessKey: 'technology' },
    { icon: '💵', text: 'Finance', path: '/portal/finance', accessKey: 'finance' },
    { icon: '📣', text: 'Marketing', path: '/portal/marketing', accessKey: 'marketing' },
    { icon: '👥', text: 'HR', path: '/portal/hr', accessKey: 'hr' },
    { icon: '🎥', text: 'Conference', path: '/portal/conference', accessKey: 'conference' },
    { icon: '🗂️', text: 'Collection', path: '/portal/collection', accessKey: 'collection' }
  ];

  // Filter menu items based on access permissions
  // When portal is locked, only show items flagged alwaysVisibleWhenLocked (Dashboard, Roadmap)
  const visibleMenuItems = menuItems.filter(item => {
    if (isPortalLocked) {
      if (item.alwaysVisibleWhenLocked !== true) return false;
      // Once the Access tab is configured, toggling the page off wins over the
      // locked-portal default (Dashboard has no toggle, so it's unaffected).
      if (item.accessKey && isLockedPageHidden(item.path)) return false;
      return true;
    }
    // Dev-only items only show in development
    if (item.devOnly) {
      return process.env.NODE_ENV === 'development';
    }
    // Pending-start clients see the full sidebar — the pending gate locks the
    // content anyway, and the tour needs every page represented. JobTread and
    // Conference aren't part of the pre-start tour, so they stay hidden.
    if (isPendingStart) {
      return item.path !== '/portal/jobtread' && item.path !== '/portal/conference';
    }
    if (item.alwaysVisible) return true;
    if (item.accessKey) {
      // For items with accessKey, check if they have access to that specific page
      return isPageAccessible(item.path);
    }
    return isPageAccessible(item.path);
  });

  // Calculate which items fit and which need to be hidden.
  // Measures real DOM heights instead of estimating so the list uses the
  // full available length and only overflows into "See More" for items that
  // genuinely don't fit. The profile row always stays visible.
  useEffect(() => {
    const calculateVisibleItems = () => {
      const sidebar = sidebarRef.current;
      const nav = navigationRef.current;
      if (!sidebar || !nav) return;

      // Actual rendered height (accounts for mobile browser UI / safe areas).
      const sidebarHeight = sidebar.clientHeight || sidebar.offsetHeight;
      if (!sidebarHeight) return;

      const topEl = sidebar.querySelector('[data-portal-sidebar-top]');
      const profileEl = sidebar.querySelector('[data-sidebar-profile]');
      const seeMoreEl = sidebar.querySelector('[data-sidebar-seemore]');
      const itemEls = nav.querySelectorAll('[data-nav-item]');

      const topHeight = topEl ? topEl.offsetHeight : (isCollapsed ? 132 : 200);
      const profileHeight = profileEl ? profileEl.offsetHeight : 60;
      const seeMoreHeight = seeMoreEl ? seeMoreEl.offsetHeight : (isCollapsed ? 52 : 48);

      // Per-item stride includes vertical margins between rows.
      let itemHeight;
      if (itemEls.length >= 2) {
        itemHeight =
          itemEls[1].getBoundingClientRect().top -
          itemEls[0].getBoundingClientRect().top;
      } else if (itemEls.length === 1) {
        itemHeight = itemEls[0].getBoundingClientRect().height + (isCollapsed ? 8 : 4);
      } else {
        itemHeight = isCollapsed ? 56 : 48;
      }
      if (!itemHeight) return;

      // Space for items when NOT showing "See More" — invariant to whether the
      // See More row is currently rendered (top chrome + profile never change).
      const baseAvailable = sidebarHeight - topHeight - profileHeight;
      const maxWithoutSeeMore = Math.floor(baseAvailable / itemHeight);

      if (visibleMenuItems.length <= maxWithoutSeeMore) {
        setVisibleItems(visibleMenuItems);
        setHiddenItems([]);
      } else {
        // Reserve the See More row exactly once (no extra -1).
        const maxWithSeeMore = Math.max(
          0,
          Math.floor((baseAvailable - seeMoreHeight) / itemHeight)
        );
        setVisibleItems(visibleMenuItems.slice(0, maxWithSeeMore));
        setHiddenItems(visibleMenuItems.slice(maxWithSeeMore));
      }
    };

    const rafId = requestAnimationFrame(calculateVisibleItems);
    const timeoutId = setTimeout(calculateVisibleItems, 150);
    const timeoutId2 = setTimeout(calculateVisibleItems, 350); // mobile browser UI settles
    window.addEventListener('resize', calculateVisibleItems);
    const onOrientation = () => setTimeout(calculateVisibleItems, 300);
    window.addEventListener('orientationchange', onOrientation);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && navigationRef.current) {
      resizeObserver = new ResizeObserver(calculateVisibleItems);
      resizeObserver.observe(navigationRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      window.removeEventListener('resize', calculateVisibleItems);
      window.removeEventListener('orientationchange', onOrientation);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [visibleMenuItems, isCollapsed]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (seeMoreRef.current && !seeMoreRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMoreMenu]);

  const isItemActive = (path) => {
    const current = location.pathname.replace(/\/$/, '') || '/portal';
    if (path === '/portal') return current === '/portal';
    return current === path || current.startsWith(`${path}/`);
  };

  return (
    <div
      ref={sidebarRef}
      className={`h-screen bg-white dark:bg-gray-800 shadow-md flex flex-col justify-between fixed top-0 left-0 transition-all duration-300 ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}
      style={{ 
        zIndex: 1000, 
        height: '100dvh', // Use dynamic viewport height for better mobile support
        maxHeight: '100dvh',
        overflow: 'visible' 
      }}
    >
      {/* Collapse/Expand Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 shadow-lg flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 z-50"
        style={{ 
          right: isCollapsed ? '-16px' : '-16px',
          transform: 'translateY(-50%)'
        }}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <FaChevronRight className="text-gray-600 dark:text-gray-300 text-sm" />
        ) : (
          <FaChevronLeft className="text-gray-600 dark:text-gray-300 text-sm" />
        )}
      </button>
      {/* Top Section */}
      <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden min-h-0">
        <div data-portal-sidebar-top className="flex-shrink-0">
          {/* Logo */}
          <div className="flex justify-center items-center py-6 px-4 border-b border-gray-100 dark:border-gray-700">
            <Link to="/portal" className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
              <img 
                src="/bba-emblem.svg" 
                alt="BBA Emblem" 
                className={`${isCollapsed ? 'h-10' : 'h-12'} w-auto object-contain`}
              />
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">Builders</span>
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">Breakthrough</span>
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">Advisors</span>
                </div>
              )}
            </Link>
          </div>

          {isCollapsed ? (
            <div className="flex justify-center py-2 border-b border-gray-100 dark:border-gray-700">
              <button
                type="button"
                className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-gray-700 transition-colors text-xl leading-none"
                aria-label="Search portal pages"
                onClick={() => {
                  setIsCollapsed(false);
                  setTimeout(() => searchInputRef.current?.focus(), 320);
                }}
              >
                <span aria-hidden="true">🔍</span>
              </button>
            </div>
          ) : (
            <div
              ref={searchWrapRef}
              className="px-3 py-3 border-b border-gray-100 dark:border-gray-700"
            >
              <label htmlFor="portal-sidebar-search" className="sr-only">
                Search portal pages and materials
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm leading-none" aria-hidden="true">🔍</span>
                <input
                  ref={searchInputRef}
                  id="portal-sidebar-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Search pages & material"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setSearchQuery('');
                      setSearchFocused(false);
                      e.target.blur();
                    }
                  }}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-3 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-red-300 focus:ring-2 focus:ring-red-200 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-100 dark:placeholder:text-gray-400 dark:focus:border-red-900/60 dark:focus:ring-red-900/40 transition-colors"
                />
                {searchFocused && searchQuery.trim().length > 0 && !portalAccessLoading && searchResults.length > 0 && (
                  <ul
                    className="absolute left-0 right-0 top-full z-[1001] mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                    role="listbox"
                  >
                    {searchResults.map((item) => (
                      <li key={item.path} role="option">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left transition-colors hover:bg-red-50 dark:hover:bg-gray-700"
                          onClick={() => {
                            navigate(item.path);
                            setSearchQuery('');
                            setSearchFocused(false);
                          }}
                        >
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                            {item.title}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{item.section}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {searchFocused && searchQuery.trim().length > 0 && !portalAccessLoading && searchResults.length === 0 && (
                  <div className="absolute left-0 right-0 top-full z-[1001] mt-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-lg dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    No matches
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Navigation */}
        <div ref={navigationRef} className="py-2 overflow-y-auto flex-1 min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {visibleItems.map((item, index) => {
            const active = isItemActive(item.path);
            return (
              <div
                key={index}
                data-nav-item
                data-active={active || undefined}
                className={`group flex items-center rounded-xl transition-colors duration-200 cursor-pointer ${
                  isCollapsed ? 'justify-center mx-2 my-1 py-3 px-2' : 'mx-3 my-0.5 py-2.5 px-3'
                } ${
                  active
                    ? 'bg-red-50 dark:bg-gray-700/70'
                    : 'hover:bg-red-50/60 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => navigate(item.path)}
                title={isCollapsed ? item.text : ''}
              >
                <span
                  className={`flex items-center justify-center leading-none ${
                    isCollapsed ? 'text-2xl' : 'text-xl mr-3 w-7'
                  }`}
                  aria-hidden="true"
                >
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <span className={`text-[17px] tracking-tight ${
                    active ? 'font-semibold' : 'font-medium'
                  } ${
                    item.path === '/portal/conference'
                      ? 'text-orange-500 dark:text-orange-400'
                      : active
                        ? 'text-red-600 dark:text-red-300'
                        : 'text-gray-700 dark:text-gray-200'
                  }`}>{item.text}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Section - Always visible */}
      <div 
        className="relative flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700" 
        ref={seeMoreRef}
        style={{ 
          minHeight: hiddenItems.length > 0 ? '108px' : '60px', // Account for "See More" button if present
          flexShrink: 0 
        }}
      >
        {/* See More dropdown */}
        {hiddenItems.length > 0 && showMoreMenu && (
          <div className={`absolute bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] z-50 ${
            isCollapsed 
              ? 'bottom-0 left-full ml-2 w-48' 
              : 'bottom-full left-0 right-0 mb-1'
          }`}>
            {hiddenItems.map((item, index) => (
              <div
                key={index}
                className="flex items-center py-3 px-5 transition-colors duration-200 cursor-pointer hover:bg-red-50 dark:hover:bg-gray-700"
                onClick={() => {
                  navigate(item.path);
                  setShowMoreMenu(false);
                }}
              >
                <span className="mr-3 text-xl leading-none w-6 flex items-center justify-center" aria-hidden="true">
                  {item.icon}
                </span>
                <span className={`text-base font-medium tracking-tight ${
                  item.path === '/portal/conference'
                    ? 'text-orange-500 dark:text-orange-400'
                    : 'text-gray-700 dark:text-gray-200'
                }`}>{item.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* See More: click toggles open/closed only (no hover — avoids flicker when moving to the popup, especially collapsed) */}
        {hiddenItems.length > 0 && (
          <button
            type="button"
            data-sidebar-seemore
            className={`w-full border-t border-gray-100 dark:border-gray-700 text-left transition-colors duration-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
              isCollapsed ? 'py-3 px-2 flex justify-center items-center' : 'py-3 px-4 flex items-center justify-between'
            }`}
            onClick={() => setShowMoreMenu((open) => !open)}
            aria-expanded={showMoreMenu}
            aria-haspopup="true"
            title={isCollapsed ? `See More (${hiddenItems.length})` : undefined}
          >
            {!isCollapsed && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                See More ({hiddenItems.length})
              </span>
            )}
            <FaChevronDown className={`text-gray-500 dark:text-gray-400 transition-transform duration-200 shrink-0 ${showMoreMenu ? 'rotate-180' : ''} ${isCollapsed ? 'text-xl' : ''}`} />
          </button>
        )}

        {/* User Profile */}
        <div
          data-sidebar-profile
          className={`border-t border-gray-100 dark:border-gray-700 transition-colors duration-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
            isCollapsed ? 'py-3 px-2 flex justify-center' : 'py-3 px-4 flex'
          }`}
          onClick={() => {
            setShowMoreMenu(false);
            navigate('/portal/profile');
          }}
          title={isCollapsed ? (loading ? 'Loading...' : 
            userProfile?.firstName && userProfile?.lastName 
              ? `${userProfile.firstName} ${userProfile.lastName}`
              : userProfile?.firstName 
              ? userProfile.firstName
              : userProfile?.displayName || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Guest User'
          ) : ''}
        >
          <div className="h-9 w-9 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden flex items-center justify-center flex-shrink-0">
            {loading ? (
              <FaUserCircle className="h-full w-full text-gray-400 dark:text-gray-300 p-1" />
            ) : userProfile?.profileImage ? (
              <img 
                src={userProfile.profileImage} 
                alt="User" 
                className="h-full w-full object-cover" 
              />
            ) : (
              <FaUserCircle className="h-full w-full text-gray-400 dark:text-gray-300 p-1" />
            )}
          </div>
          {!isCollapsed && (
            <div className="ml-3 flex flex-col min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                {loading ? 'Loading...' : 
                  userProfile?.firstName && userProfile?.lastName 
                    ? `${userProfile.firstName} ${userProfile.lastName}`
                    : userProfile?.firstName 
                    ? userProfile.firstName
                    : userProfile?.displayName || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Guest User'
                }
              </div>
              {(userProfile?.company || accountData?.company) && (
                <div
                  className="text-xs font-medium truncate mt-0.5"
                  style={{
                    color: userProfile?.companyColors?.primary || accountData?.companyColors?.primary || '#6b7280'
                  }}
                >
                  {userProfile?.company || accountData?.company}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalSidebar; 